import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon, Building2, Sun, BatteryCharging, Plug, Zap, Wrench, Thermometer, CheckCircle2 } from "lucide-react";
import { SERVICES, CONTACT } from "../data/site";

const ICONS = { Home: HomeIcon, Building2, Sun, BatteryCharging, Plug, Zap, Wrench, Thermometer };

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="py-24 md:py-32 border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline"><span className="text-[#E62117] mr-2">◆</span>Services we offer</p>
          <h1 data-testid="services-title" className="font-display text-6xl md:text-8xl lg:text-9xl uppercase leading-[0.88] mt-6">
            Everything<br />
            <span className="text-[#E62117]">Electrical.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-zinc-700 max-w-2xl">
            Eight core services covering residential, commercial, renewables and climate.
            One licensed team. One point of contact. No surprises.
          </p>
        </div>
      </section>

      {/* Service list */}
      {SERVICES.map((s, i) => {
        const Icon = ICONS[s.icon] || Zap;
        const reverse = i % 2 === 1;
        return (
          <section
            key={s.slug}
            data-testid={`service-section-${s.slug}`}
            className={`py-20 md:py-28 border-b-2 border-black ${i % 2 === 0 ? "bg-white" : "bg-zinc-50"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:flex-row-reverse" : ""}`}>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="border-2 border-black overflow-hidden aspect-[4/3] bg-zinc-200 relative">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-white border-2 border-black px-3 py-1 font-mono-brand text-xs font-bold uppercase">
                      0{i + 1} / 0{SERVICES.length}
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 border-2 border-black flex items-center justify-center bg-[#E62117] text-white">
                      <Icon size={26} />
                    </div>
                    <p className="overline">{s.title.split(" ")[0]}</p>
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
                    {s.title}
                  </h2>
                  <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-lg">
                    {s.long}
                  </p>
                  <ul className="mt-6 space-y-2">
                    {[
                      "Fixed-price quotes in writing",
                      "Certificate of Electrical Safety",
                      "Manufacturer-backed warranty",
                    ].map((li) => (
                      <li key={li} className="flex items-center gap-3 text-zinc-800">
                        <CheckCircle2 size={18} className="text-[#E62117] flex-shrink-0" />
                        <span className="font-medium">{li}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`service-cta-${s.slug}`} className="btn-primary mt-8">
                    Enquire About {s.title.split(" ")[0]} <ArrowUpRight className="ml-2" size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-24 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-5xl md:text-7xl uppercase">Not sure what you need?</h2>
          <p className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto">Give us a call — Andrew will scope it out and point you the right way.</p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="services-cta-call" className="btn-primary">Call {CONTACT.phone}</a>
            <Link to="/contact" data-testid="services-cta-contact" className="btn-ghost-dark">Send a Message</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
